import { message } from 'antd';
import UndoNotification from './UndoNotification';
import { useSpin } from '../../../components/SpinContext';
import { shipmentMassActions } from '../../../apis/shipment';
import { showCustomSuccessMessage } from '../../../common/utils/messageUtils';
import { splitCalendarScopedFilters } from './utils';

export type MassActionType =
  | 'mark_as_delivered'
  | 'mark_as_resolved'
  | 'dismiss_all_alerts'
  | 'archive'
  | 'update_teams';

interface ShipmentMassActionPayload {
  action: MassActionType;
  shipment_ids?: string[];
  select_all?: boolean;
  exclude_shipment_ids?: string[];
  filters?: Record<string, unknown>;
  start?: string;
  end?: string;
  view_by?: string;
}

export interface MassActionApiResponse {
  status?: number;
  data?: {
    data?: {
      is_success?: boolean;
      message?: string;
      status?: string;
      task_id?: string;
    };
  };
}

export interface MassActionParams<TPayload = ShipmentMassActionPayload> {
  action: MassActionType;
  shipmentIds: string[];
  token: string;
  successMessage: string;
  errorMessage: string;
  onSuccess?: () => void;
  onUndo?: () => void;
  entityLabel?: 'shipment' | 'row';
  buildPayload?: () => TPayload;
  countForUndo?: number;
  execute?: (
    token: string,
    payload: TPayload
  ) => Promise<MassActionApiResponse>;
  selectAll?: boolean;
  filtersPayload?: Record<string, unknown>;
  search?: string;
  excludeShipmentIds?: string[];
}

interface UseMassActionReturn {
  handleMassAction: <TPayload = ShipmentMassActionPayload>(
    params: MassActionParams<TPayload>
  ) => void;
}

export const useMassAction = (): UseMassActionReturn => {
  const { showSpin, hideSpin } = useSpin();

  const runMassAction = <TPayload,>({
    action,
    shipmentIds,
    token,
    errorMessage,
    onSuccess,
    execute,
    buildPayload,
    selectAll,
    filtersPayload,
    search,
    excludeShipmentIds,
  }: MassActionParams<TPayload>) => {
    showSpin();

    const payload = buildPayload
      ? buildPayload()
      : selectAll
        ? (() => {
            const { filters, start, end, view_by } =
              splitCalendarScopedFilters(filtersPayload);
            return {
              action,
              select_all: true,
              exclude_shipment_ids: excludeShipmentIds,
              filters: {
                ...filters,
                search: (search ?? '').trim(),
              },
              ...(start ? { start } : {}),
              ...(end ? { end } : {}),
              ...(view_by ? { view_by } : {}),
            } as unknown as TPayload;
          })()
        : ({
            action,
            shipment_ids: shipmentIds,
          } as unknown as TPayload);

    const runner =
      execute ??
      (shipmentMassActions as unknown as (
        token: string,
        payload: TPayload
      ) => Promise<MassActionApiResponse>);

    runner(token, payload)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          showCustomSuccessMessage(data?.message);
          onSuccess?.();
        } else {
          message.error(data?.message || errorMessage);
        }
      })
      .catch(() => {
        message.error(errorMessage);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const handleMassAction = <TPayload,>({
    action,
    shipmentIds,
    token,
    successMessage,
    errorMessage,
    onSuccess,
    onUndo,
    entityLabel = 'shipment',
    execute,
    buildPayload,
    countForUndo,
    selectAll = false,
    filtersPayload,
    search,
    excludeShipmentIds = [],
  }: MassActionParams<TPayload>) => {
    if (selectAll) {
      runMassAction({
        action,
        shipmentIds,
        token,
        successMessage,
        errorMessage,
        onSuccess,
        onUndo,
        entityLabel,
        execute,
        buildPayload,
        countForUndo,
        selectAll,
        filtersPayload,
        search,
        excludeShipmentIds,
      });
      return;
    }

    const key = `${action}-${Date.now()}`;
    let undoCalled = false;

    const timer = setTimeout(() => {
      if (undoCalled) return;
      runMassAction({
        action,
        shipmentIds,
        token,
        successMessage,
        errorMessage,
        onSuccess,
        onUndo,
        entityLabel,
        execute,
        buildPayload,
        countForUndo,
        selectAll,
        filtersPayload,
        search,
        excludeShipmentIds,
      });
    }, 5000);

    const count = countForUndo ?? shipmentIds.length;
    const entity = count === 1 ? entityLabel : `${entityLabel}s`;
    const undoDescription =
      action === 'update_teams'
        ? `${count} ${entity} updated. Undo (5s)`
        : `${count} ${entity} marked ${
            action === 'mark_as_delivered'
              ? 'Delivered'
              : action === 'mark_as_resolved'
                ? 'Resolved'
                : action === 'dismiss_all_alerts'
                  ? 'Dismissed'
                  : 'Archived'
          }. Undo (5s)`;

    UndoNotification({
      key,
      message: (
        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {successMessage}
        </div>
      ),
      description: <div style={{ fontSize: '12px' }}>{undoDescription}</div>,
      onUndo: () => {
        undoCalled = true;
        clearTimeout(timer);
        onUndo?.();
      },
    });
  };

  return { handleMassAction };
};
